/**
 * Small pure helpers for rendering an estimate row in a list — shared by the
 * account page and the retailer admin area. Labels for known modes / space
 * types come from the translated dictionaries; anything unknown is prettified
 * so an old row under a retired value still reads as words.
 */

import { calculationModeLabel, spaceTypeLabel } from '../components/results/configDataPoints';

/** "FullRoom" → "Full Room", "living-space" → "Living Space"; null-safe. */
export const prettify = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[-_\s]+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/** "Full Room | Garage" — either side may be missing; null when both are. */
export const modeAndSpaceLabel = (mode: string | null | undefined, space: string | null | undefined): string | null => {
  const m = mode ? calculationModeLabel(mode) : null;
  const s = space ? spaceTypeLabel(space, prettify(space) ?? undefined) : null;
  const parts = [m, s].filter(Boolean);
  return parts.length ? parts.join(' | ') : null;
};

/** "Kitchener, Ontario" / one side / "—". */
export const locationLabel = (city: string | null | undefined, region: string | null | undefined): string =>
  city && region ? `${city}, ${region}` : city || region || '—';

/** Server wall-clock ("2026-10-20 14:03:00") → localized date + time in the viewer's language. */
export const formatDateTime = (dateStr: string | null | undefined, locale: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr.replace(' ', 'T'));
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Date only. */
export const formatDate = (dateStr: string | null | undefined, locale: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr.replace(' ', 'T'));
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString(locale);
};
